import { useId, useState, type FormEvent } from "react";
import type { AnswerForm as AnswerFormSchema } from "../../../packages/contracts/src/production";
import { Button } from "./components/ui/button";
import "./answer-form.css";
import { buildAnswer, fieldsOf, label, type Field } from "./answer-form";

export { buildAnswer };

/** A form made from the JSON Schema of an ask step. */
export function AnswerForm({ form, onSubmit, pending = false, error }: {
  form: AnswerFormSchema | undefined;
  onSubmit: (answer: Record<string, unknown>) => void;
  pending?: boolean;
  /** An error message from the server. */
  error?: string | null;
}) {
  const id = useId();
  const fields = fieldsOf(form);
  const [values, setValues] = useState<Record<string, string | boolean>>({});
  const [invalid, setInvalid] = useState<string | null>(null);
  const set = (name: string, value: string | boolean) => { setValues(current => ({ ...current, [name]: value })); setInvalid(null); };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const result = buildAnswer(form, values);
    if ("error" in result) { setInvalid(result.error); return; }
    setInvalid(null);
    onSubmit(result.answer);
  };
  const message = invalid ?? error;

  return <form className="answer-form" onSubmit={submit} noValidate>
    {fields.map(field => {
      const inputId = `${id}-${field.name}`;
      const description = typeof field.schema.description === "string" ? field.schema.description : null;
      const help = description ? <small id={`${inputId}-help`}>{description}</small> : null;
      const describedBy = description ? `${inputId}-help` : undefined;
      const text = typeof values[field.name] === "string" ? values[field.name] as string : "";
      if (field.kind === "boolean") return <div className="answer-field answer-field-check" key={field.name}>
        <label htmlFor={inputId}>
          <input id={inputId} type="checkbox" checked={values[field.name] === true} aria-describedby={describedBy} onChange={event => set(field.name, event.target.checked)} />
          <span>{label(field.name)}{field.required && <span className="answer-required" aria-hidden="true"> *</span>}</span>
        </label>
        {help}
      </div>;
      const enumValues = field.kind === "string" && Array.isArray(field.schema.enum) ? field.schema.enum.filter((item): item is string => typeof item === "string") : null;
      return <div className="answer-field" key={field.name}>
        <label htmlFor={inputId}>{label(field.name)}{field.required && <span className="answer-required" aria-hidden="true"> *</span>}</label>
        {help}
        {enumValues ? <select id={inputId} value={text} required={field.required} aria-describedby={describedBy} onChange={event => set(field.name, event.target.value)}>
          <option value="">Select a value</option>
          {enumValues.map(item => <option key={item} value={item}>{item}</option>)}
        </select>
          : field.kind === "string" ? <textarea id={inputId} rows={2} value={text} required={field.required} aria-describedby={describedBy} onChange={event => set(field.name, event.target.value)} />
          : field.kind === "number" || field.kind === "integer" ? <input id={inputId} type="number" inputMode="decimal" step={field.kind === "integer" ? 1 : "any"} value={text} required={field.required} aria-describedby={describedBy} onChange={event => set(field.name, event.target.value)} />
          : <textarea id={inputId} className="answer-json" rows={4} spellCheck={false} placeholder={field.schema.type === "array" ? "[ ]" : "{ }"} value={text} required={field.required} aria-describedby={describedBy} onChange={event => set(field.name, event.target.value)} />}
      </div>;
    })}
    {fields.some(field => field.required) && <p className="answer-note">* Necessary</p>}
    {message && <p className="answer-error" role="alert">{message}</p>}
    <Button type="submit" disabled={pending}>{pending ? "Sending" : "Send the answer"}</Button>
  </form>;
}
